import {Component, DestroyRef, HostListener, Input, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {AuthService} from '../../_services/auth.service';
import {User} from '../../_types/user';
import {Badge} from '../../_types/rewards';
import {RewardsService} from '../../_services/rewards.service';
import {combineLatest, distinctUntilChanged, EMPTY, Observable, startWith, switchMap, take} from 'rxjs';
import {Community} from '../../_types/communities';
import {LocalStorageService} from '../../_services/local-storage.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ThemeService} from "../../_services/theme.service";
import {CommunitiesService} from "../../_services/communities.service";

@Component({
    selector: 'app-badge-progress',
    templateUrl: './badge-progress.component.html',
    styleUrl: './badge-progress.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BadgeProgressComponent implements OnInit {
    @Input({required: true}) user!: User;
    @Input() showInProgress: boolean = false;
    @Input() small: boolean = false;
    @Input() limit: number = 3;
    @Input() hideTooltip: boolean = false;

    userBadges: Badge[] = [];
    allBadges: Badge[] = [];
    inProgressBadges: Badge[] = [];
    community: Community | null = null;

    constructor(
        private readonly authService: AuthService,
        private readonly rewardsService: RewardsService,
        private readonly communitiesService: CommunitiesService,
        private readonly localStorageService: LocalStorageService,
        private readonly destroyRef: DestroyRef,
        protected themeService: ThemeService
    ) {
        this.communitiesService.getCurrentDashboardCommunity().subscribe(r => {
            this.community = r;
        });
    }

    get currentUser(): User {
        return this.authService.getUser();
    }

    get isMe(): boolean {
        return this.user.id === this.currentUser.id;
    }

    get lastSelectedCommunity(): Community | null {
        return this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null);
    }

    get badges(): Badge[] {
        return this.showInProgress ? this.inProgressBadges : this.userBadges;
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any): void {
        this.checkInnerWidth();
    }

    checkInnerWidth(): void {
        if (this.small) {
            if (window.innerWidth > 650) {
                this.limit = 4;
            } else if (window.innerWidth < 650 && window.innerWidth > 500) {
                this.limit = 3;
            } else if (window.innerWidth > 300) {
                this.limit = 2;
            } else {
                this.limit = 1;
            }
        }
    }

    getBadges(): void {
        this.checkInnerWidth();

        this.communitiesService.getCurrentDashboardCommunity().pipe(
            startWith(this.lastSelectedCommunity),
            distinctUntilChanged((prev, cur): boolean => prev?.id === cur?.id),
            takeUntilDestroyed(this.destroyRef),
            switchMap(res => {
                this.userBadges = [];
                if (!res) return EMPTY;
                const userBadges$: Observable<Badge[]> = this.isMe ? this.rewardsService.getCurrentUserBadges(res!.id).pipe(take(1)) : this.rewardsService.getUserBadges(res!.id, this.user.id).pipe(take(1));
                const allBadges$: Observable<Badge[]> = this.rewardsService.getAllBadges(res.id).pipe(take(1));
                return combineLatest([userBadges$, allBadges$])
            }),
        ).subscribe(res => {
            this.userBadges = res[0];
            this.allBadges = res[1] || [];
            this.inProgressBadges = []

            if (this.showInProgress) {
                for (let badge of this.allBadges) {
                    const i: number = this.userBadges.findIndex((x: any): boolean => x.id === badge.id);
                    // Inprogress badges are badges the user does not yet own. Unless the badge is stackable
                    if (badge.stackable || i == null || i < 0) {
                        const userBadge = this.userBadges[i];
                        if (i != null && i >= 0 && badge.requirements && userBadge?.stack_count) {
                            let alteredBadge = badge
                            for (let requirement of badge.requirements) {
                                let totalBeforeLastBadgeReceival: number = requirement.quantity * userBadge?.stack_count
                                requirement.updatedQuantity = requirement.currentQuantity - totalBeforeLastBadgeReceival
                            }
                            this.inProgressBadges.push(alteredBadge)
                        } else {
                            this.inProgressBadges.push(badge)
                        }
                    }
                }
            }
        });
    }

    ngOnInit(): void {
        this.getBadges();
    }

    trackByBadge(index: number, badge: any): string {
        return badge.title;
    }

    getPercent(value: number, requiredPoints: number): number {
        return (value * 100) / (requiredPoints || 1);
    }
}
